import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfigurationService, PathsService } from '@draeger-academy/common';
import { AuthenticationService } from '@draeger-academy/common/lib/services/authentication/authentication.service';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { User } from 'models/user';
import { BehaviorSubject, Observable } from 'rxjs';
import { first } from 'rxjs/operators';
import { FormsUtils } from '../form-builder';

@Component({
    selector: 'da-login-form',
    templateUrl: './login-form.component.html',
    styleUrls: ['./login-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoginFormComponent implements OnInit {
    public loginForm: UntypedFormGroup;
    loading = false;
    submitted = false;
    remember = false;
    showError = false;
    @Input() targetUrl: string;
    @Input() isCheckout: string;
    @Output()
    checkoutProceed: EventEmitter<string> = new EventEmitter<string>();
    hasFocusUsername = false;
    hasFocusPassword = false;
    user: User = {
        id: 0,
        username: '',
        password: '',
        token: ''
    };
    private _$showRememberMe: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _$showForgotPassword: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _$showError: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _$showError401: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public linkForgotPassword: string;
    public currentPortal: string;

    private _$portalId: BehaviorSubject<string> = new BehaviorSubject(null);
    public get portalId$(): Observable<string> {
        return this._$portalId.asObservable();
    }

    constructor(
        private formBuilder: UntypedFormBuilder,
        private router: Router,
        private authenticationService: AuthenticationService,
        private _localizeRouterService: LocalizeRouterService,
        private _configurationService: ConfigurationService,
        private _activatedRoute: ActivatedRoute,
        private _pathsService: PathsService
    ) {}

    public get showRememberMe$(): Observable<boolean> {
        return this._$showRememberMe.asObservable();
    }

    public get showForgotPassword$(): Observable<boolean> {
        return this._$showForgotPassword.asObservable();
    }

    public get showError$(): Observable<boolean> {
        return this._$showError.asObservable();
    }
    public get showError401$(): Observable<boolean> {
        return this._$showError401.asObservable();
    }

    // convenience getter for easy access to form fields
    get f() {
        return this.loginForm.controls;
    }

    ngOnInit() {
        this.linkForgotPassword = this._pathsService.getForgotPasswordLink();
        this._$showForgotPassword.next(this._configurationService.hasFeature('ForgotPassword'));
        this._$showRememberMe.next(this._configurationService.hasFeature('RememberMe'));
        this.checkFeaturesOnPortals();

        this.loginForm = this.formBuilder.group({
            username: ['', [Validators.required, Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,10}$')]],
            password: ['', Validators.required],
            remember: [false]
        });
    }

    public hasValue(fieldname) {
        const _input = fieldname;
        return _input.value ? FormsUtils.trim(_input.value.toString()) !== '' : false;
    }

    onSubmit() {
        this.submitted = true;

        // stop here if form is invalid
        if (this.loginForm.invalid) {
            return;
        }

        this.loading = true;
        this.authenticationService
            .login(this.f.username.value, this.f.password.value, this.f.remember.value)
            .pipe(first())
            .subscribe(
                (data) => {
                    if (data.token) {
                        const _snapshotUrl = this._activatedRoute.snapshot.queryParams['returnUrl'];
                        this.router.navigate([_snapshotUrl ? _snapshotUrl : this.targetUrl]);
                    } else {
                        this.showError = true;
                        this._$showError.next(true);
                    }

                    this.loading = false;
                },
                (error) => {
                    if (error.status === 401) {
                        this._$showError401.next(true);
                    } else {
                        this.showError = true;
                        this._$showError.next(true);
                    }
                    console.log(error);
                    this.loading = false;
                }
            );
    }

    onCheckboxChange(remember) {
        this.remember = remember;
    }

    cancel() {
        if (!this.isCheckout) {
            this.router.navigate([this._pathsService.getHomeLink()]);
        } else {
            localStorage.setItem('showLogin', 'false');
            this.checkoutProceed.emit('false');
        }
    }

    checkFeaturesOnPortals() {
        const isPortal = this._configurationService.isPartnerPage();

        if (isPortal) {
            this._configurationService.getConfigPartnerByPortal().then((values) => {
                if (values) {
                    const tempFeatures = values.filter((setting) => setting.key.toLowerCase() === 'features');
                    let hasForgotPassword;
                    if (tempFeatures && tempFeatures[0] && tempFeatures[0].value && tempFeatures[0].value.length > 0) {
                        hasForgotPassword = tempFeatures[0].value.filter((setting) => setting.name === 'ForgotPassword');
                    }
                    if (hasForgotPassword && hasForgotPassword[0] && hasForgotPassword[0].enabled) {
                        this._$showForgotPassword.next(true);
                    }

                    let hasRememberMe;
                    if (tempFeatures && tempFeatures[0] && tempFeatures[0].value && tempFeatures[0].value.length > 0) {
                        hasRememberMe = tempFeatures[0].value.filter((setting) => setting.name === 'RememberMe');
                    }
                    if (hasRememberMe && hasRememberMe[0] && hasRememberMe[0].enabled) {
                        this._$showRememberMe.next(true);
                    }
                }
            });
        }
    }
}
